import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase/config';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, deleteDoc, doc, updateDoc, writeBatch, getDocs } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { Chess } from 'chess.js';

const PIECE_IMAGES = { 'wR': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wR.svg', 'wN': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wN.svg', 'wB': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wB.svg', 'wQ': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wQ.svg', 'wK': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wK.svg', 'wP': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wP.svg', 'bR': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/bR.svg', 'bN': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/bN.svg', 'bB': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/bB.svg', 'bQ': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/bQ.svg', 'bK': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/bK.svg', 'bP': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/bP.svg' };
const baseRows = [8, 7, 6, 5, 4, 3, 2, 1]; const baseCols = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

// ==========================================
// 🚀 THE NEW IMMERSIVE PGN READER COMPONENT
// ==========================================
const InteractivePGNReader = ({ item, items = [], onClose, onNavigate, onAssign, activeClassId, syncToLiveBoard }) => {
    const [fens, setFens] = useState([]);
    const [moves, setMoves] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [header, setHeader] = useState({});

    const currentIndex = items.findIndex(i => i.id === item.id);
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < items.length - 1;

    const handlePrevItem = () => {
        if (hasPrev && onNavigate) {
            onNavigate(items[currentIndex - 1]);
        }
    };

    const handleNextItem = () => {
        if (hasNext && onNavigate) {
            onNavigate(items[currentIndex + 1]);
        }
    };

    useEffect(() => {
        const temp = new Chess();
        if (item.type === 'pgn') {
            try { temp.loadPgn(item.content); } catch(e){}
        } else {
            try { 
                temp.load(item.content); 
                const solMoves = typeof item.answer === 'string' 
                    ? item.answer.replace(/,/g, ' ').replace(/\s+/g, ' ').trim().split(' ') 
                    : item.answer;
                solMoves.forEach(m => {
                    if (m.trim()) temp.move(m.trim());
                });
            } catch(e){}
        }

        const replayer = new Chess();
        if (item.type === 'pgn') {
            const h = temp.header();
            setHeader(h);
            if (h.FEN) replayer.load(h.FEN);
        } else {
            replayer.load(item.content);
        }

        const history = temp.history({ verbose: true });
        const fenList = [replayer.fen()];
        history.forEach(m => {
            replayer.move(m);
            fenList.push(replayer.fen());
        });

        setFens(fenList);
        setMoves(history);
        setCurrentStep(0);
    }, [item]);

    const renderFen = fens[currentStep] || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    const tempBoard = new Chess(renderFen);
    const render = {};
    tempBoard.board().forEach((row, r) => row.forEach((sq, c) => {
        if(sq) render[`${baseCols[c]}${baseRows[r]}`] = sq.color + sq.type.toUpperCase();
    }));

    return (
        <div style={{position: 'fixed', inset: 0, background: 'var(--bg-primary)', zIndex: 9999, display: 'flex', flexDirection: 'column', animation: 'fadeIn 0.3s' }}>
            {/* Header */}
            <div style={{padding: '15px 30px', background: 'var(--bg-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(163, 177, 198, 0.4)' }}>
                <div>
                    <h2 style={{margin: 0, color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '10px' }}>📖 PGN Study Reader</h2>
                    <p style={{margin: '5px 0 0 0', color: 'var(--text-primary)', fontWeight: 'bold' }}>{header.White ? `${header.White} vs ${header.Black}` : item.title}</p>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <button 
                        onClick={handlePrevItem} 
                        disabled={!hasPrev} 
                        style={{
                            padding: '10px 18px', 
                            background: hasPrev ? '#3b82f6' : 'rgba(163, 177, 198, 0.2)', 
                            color: hasPrev ? 'white' : 'var(--text-secondary)', 
                            border: '1px solid rgba(163, 177, 198, 0.4)', 
                            borderRadius: '8px', 
                            fontWeight: 'bold', 
                            cursor: hasPrev ? 'pointer' : 'not-allowed', 
                            opacity: hasPrev ? 1 : 0.6,
                            transition: 'all 0.2s'
                        }}
                    >
                        ⬅️ Prev Position
                    </button>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '14px', background: 'rgba(59, 130, 246, 0.1)', padding: '6px 12px', borderRadius: '20px', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                        {currentIndex >= 0 ? `${currentIndex + 1} / ${items.length}` : '- / -'}
                    </span>
                    <button 
                        onClick={handleNextItem} 
                        disabled={!hasNext} 
                        style={{
                            padding: '10px 18px', 
                            background: hasNext ? '#3b82f6' : 'rgba(163, 177, 198, 0.2)', 
                            color: hasNext ? 'white' : 'var(--text-secondary)', 
                            border: '1px solid rgba(163, 177, 198, 0.4)', 
                            borderRadius: '8px', 
                            fontWeight: 'bold', 
                            cursor: hasNext ? 'pointer' : 'not-allowed', 
                            opacity: hasNext ? 1 : 0.6,
                            transition: 'all 0.2s'
                        }}
                    >
                        Next Position ➡️
                    </button>
                    {activeClassId && syncToLiveBoard && (
                        <button 
                            onClick={() => {
                                syncToLiveBoard(item.content, item.type, item, items);
                            }}
                            style={{
                                padding: '10px 18px', 
                                background: item.type === 'lesson' ? '#3b82f6' : '#ec4899', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '8px', 
                                fontWeight: 'bold', 
                                cursor: 'pointer',
                                boxShadow: item.type === 'lesson' ? '0 2px 5px rgba(59, 130, 246, 0.2)' : '0 2px 5px rgba(236, 72, 153, 0.2)',
                                transition: 'all 0.2s'
                            }}
                            onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                            onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                            {item.type === 'lesson' ? '🎓 Load Lesson to Class' : '🚀 Launch to Board'}
                        </button>
                    )}
                    {onAssign && (
                        <button 
                            onClick={() => onAssign(item.id)} 
                            style={{
                                padding: '10px 18px', 
                                background: '#f59e0b', 
                                color: 'black', 
                                border: 'none', 
                                borderRadius: '8px', 
                                fontWeight: 'bold', 
                                cursor: 'pointer',
                                boxShadow: '0 2px 5px rgba(245, 158, 11, 0.2)',
                                transition: 'all 0.2s'
                            }}
                            onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                            onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                            📝 Assign as HW
                        </button>
                    )}
                    <div style={{ width: '1px', height: '24px', background: 'rgba(163, 177, 198, 0.4)', margin: '0 10px' }}></div>
                    <button onClick={onClose} style={{padding: '10px 20px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 2px 5px rgba(239, 68, 68, 0.2)' }}>✖ Close Reader</button>
                </div>
            </div>

            {/* Main Content Area */}
            <div style={{flex: 1, padding: '30px', display: 'flex', gap: '40px', overflowY: 'auto' }}>
                
                {/* Left: Board & Controls */}
                <div style={{flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
                    <div style={{width: '500px', height: '500px', display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gridTemplateRows: 'repeat(8, 1fr)', border: '4px solid rgba(163, 177, 198, 0.4)', borderRadius: '8px', overflow: 'hidden', boxShadow: 'var(--panel-shadow)' }}>
                        {baseRows.map((row, rIdx) => baseCols.map((col, cIdx) => {
                            const id = `${col}${row}`; const isDark = (rIdx + cIdx) % 2 === 1;
                            return (
                                <div key={id} style={{background: isDark ? '#769656' : '#eeeed2', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    {render[id] && <img src={PIECE_IMAGES[render[id]]} alt="p" style={{width: '90%' }} />}
                                </div>
                            );
                        }))}
                    </div>
                    
                    {/* Navigation Buttons */}
                    <div style={{display: 'flex', gap: '10px', background: 'var(--bg-primary)', padding: '15px', borderRadius: '12px', border: '1px solid rgba(163, 177, 198, 0.4)', width: '100%', boxSizing: 'border-box', justifyContent: 'center' }}>
                        <button onClick={() => setCurrentStep(0)} disabled={currentStep === 0} style={{padding: '12px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: currentStep === 0 ? 'not-allowed' : 'pointer', opacity: currentStep === 0 ? 0.5 : 1 }}>|&lt; Start</button>
                        <button onClick={() => setCurrentStep(s => Math.max(0, s - 1))} disabled={currentStep === 0} style={{padding: '12px 25px', background: '#10b981', color: 'black', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: currentStep === 0 ? 'not-allowed' : 'pointer', opacity: currentStep === 0 ? 0.5 : 1 }}>&lt; Prev</button>
                        <button onClick={() => setCurrentStep(s => Math.min(fens.length - 1, s + 1))} disabled={currentStep === fens.length - 1} style={{padding: '12px 25px', background: '#10b981', color: 'black', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: currentStep === fens.length - 1 ? 'not-allowed' : 'pointer', opacity: currentStep === fens.length - 1 ? 0.5 : 1 }}>Next &gt;</button>
                        <button onClick={() => setCurrentStep(fens.length - 1)} disabled={currentStep === fens.length - 1} style={{padding: '12px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: currentStep === fens.length - 1 ? 'not-allowed' : 'pointer', opacity: currentStep === fens.length - 1 ? 0.5 : 1 }}>End &gt;|</button>
                    </div>
                </div>

                {/* Right: Info, Move List & Raw PGN */}
                <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    
                    {item.type === 'lesson' ? (
                        <div style={{background: 'var(--bg-primary)', borderRadius: '12px', border: '1px solid rgba(163, 177, 198, 0.4)', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                            <div style={{padding: '15px 20px', background: 'var(--bg-primary)', borderBottom: '1px solid rgba(163, 177, 198, 0.4)', fontWeight: 'bold', color: 'var(--text-primary)' }}>Saved Lesson Notes</div>
                            <div 
                                style={{padding: '20px', overflowY: 'auto', color: 'var(--text-primary)', fontSize: '14px', lineHeight: '1.6', height: '100%', boxSizing: 'border-box'}} 
                                dangerouslySetInnerHTML={{ __html: item.answer || '<i>No notes saved.</i>' }}
                            />
                        </div>
                    ) : (
                        <>
                            {/* Move List Box */}
                            <div style={{background: 'var(--bg-primary)', borderRadius: '12px', border: '1px solid rgba(163, 177, 198, 0.4)', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                                <div style={{padding: '15px 20px', background: 'var(--bg-primary)', borderBottom: '1px solid rgba(163, 177, 198, 0.4)', fontWeight: 'bold', color: 'var(--text-primary)' }}>Game Notation</div>
                                <div style={{padding: '20px', overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: '8px', alignContent: 'flex-start' }}>
                                    {moves.map((m, idx) => (
                                        <span 
                                            key={idx} 
                                            onClick={() => setCurrentStep(idx + 1)}
                                            style={{padding: '5px 10px', 
                                                background: currentStep === idx + 1 ? '#3b82f6' : 'transparent', 
                                                color: currentStep === idx + 1 ? 'white' : 'var(--text-secondary)', 
                                                borderRadius: '6px', 
                                                cursor: 'pointer',
                                                fontWeight: currentStep === idx + 1 ? 'bold' : 'normal'
                                            }}
                                        >
                                            {idx % 2 === 0 ? `${(idx/2)+1}. ` : ''}{m.san}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Raw PGN Text Area */}
                            <div style={{background: 'var(--bg-primary)', borderRadius: '12px', border: '1px solid rgba(163, 177, 198, 0.4)', display: 'flex', flexDirection: 'column', height: '200px', flexShrink: 0 }}>
                                <div style={{padding: '12px 20px', background: 'var(--bg-primary)', borderBottom: '1px solid rgba(163, 177, 198, 0.4)', fontWeight: 'bold', color: 'var(--text-secondary)', fontSize: '14px' }}>
                                    {item.type === 'puzzle' ? 'Puzzle Details & Solution Comments' : 'Original PGN Data'}
                                </div>
                                <textarea 
                                    readOnly 
                                    value={item.type === 'puzzle' 
                                        ? `FEN: ${item.content}\n\nSolution Moves: ${item.answer || 'None'}\n\nComments:\n${item.comments || item.desc || 'No comments'}` 
                                        : item.content
                                    } 
                                    style={{flex: 1, padding: '15px', background: 'transparent', color: '#10b981', border: 'none', resize: 'none', fontFamily: 'monospace', fontSize: '13px', outline: 'none' }}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const CURRICULUM = [
    {
        phase: "Phase 1: Chess Basics (Classes 1-12)",
        classes: [
            {
                name: "Class 1: Board Coordinates & Squares",
                items: [
                    { title: "Center Squares", type: "puzzle", content: "8/8/8/3pP3/3Pp3/8/8/8 w - - 0 1", answer: "", desc: "Learn the 4 center squares." },
                    { title: "Corners of the Board", type: "puzzle", content: "8/8/8/8/8/8/8/8 w - - 0 1", answer: "", desc: "Spot the corner squares." }
                ]
            },
            {
                name: "Class 2: Rook Movements & Captures",
                items: [
                    { title: "Rook Capturing Bishop", type: "puzzle", content: "8/2b5/8/8/2R5/8/8/8 w - - 0 1", answer: "Rxc7", desc: "Use the Rook to capture the Bishop." }
                ]
            },
            {
                name: "Class 3: Bishop Movements & Diagonals",
                items: [
                    { title: "Bishop Capture", type: "puzzle", content: "7r/8/8/8/3B4/8/8/8 w - - 0 1", answer: "Bxh8", desc: "Capture the Rook on the long diagonal." }
                ]
            },
            {
                name: "Class 4: Knight Movements & L-Shapes",
                items: [
                    { title: "Knight Fork", type: "puzzle", content: "q3k3/8/8/3N4/8/8/8/8 w - - 0 1", answer: "Nc7+", desc: "Deliver a Knight fork on King and Queen." }
                ]
            },
            {
                name: "Class 5: King Movements & Safe Squares",
                items: [
                    { title: "King Safe Escape", type: "puzzle", content: "k7/8/r7/1K6/8/8/8/8 w - - 0 1", answer: "Kxa6", desc: "Move the King to a safe square by capturing the Rook." }
                ]
            },
            {
                name: "Class 6: Pawn Movements & Double Step",
                items: [
                    { title: "Pawn Double Advance", type: "puzzle", content: "8/8/8/8/8/8/4P3/8 w - - 0 1", answer: "e4", desc: "Move the pawn two squares forward." }
                ]
            },
            {
                name: "Class 7: Safe vs Unsafe Captures",
                items: [
                    { title: "Spot the Free Piece", type: "puzzle", content: "3r4/3p4/8/3B4/8/8/8/8 w - - 0 1", answer: "Bxd7", desc: "Spot the undefended pawn." }
                ]
            },
            {
                name: "Class 8: The Concept of Check",
                items: [
                    { title: "Deliver Check", type: "puzzle", content: "k7/8/8/8/4Q3/8/8/8 w - - 0 1", answer: "Qe8+", desc: "Put the black King in check." }
                ]
            },
            {
                name: "Class 9: Escape from Check",
                items: [
                    { title: "Escape Check", type: "puzzle", content: "k7/8/r7/8/3B4/8/1K6/8 w - - 0 1", answer: "Kb3", desc: "Move the king out of check." }
                ]
            },
            {
                name: "Class 10: Special Move - Castling",
                items: [
                    { title: "Castle Kingside", type: "puzzle", content: "rnbqk2r/pppp1ppp/5n2/4p3/1bB1P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 4 4", answer: "O-O", desc: "Castle your King to safety." }
                ]
            },
            {
                name: "Class 11: Special Move - En Passant",
                items: [
                    { title: "En Passant Capture", type: "puzzle", content: "8/8/8/4pP2/8/8/8/8 w - e6 0 1", answer: "fxe6", desc: "Perform en passant capture." }
                ]
            },
            {
                name: "Class 12: Special Move - Pawn Promotion",
                items: [
                    { title: "Promote to Queen", type: "puzzle", content: "5k2/4P3/5K2/8/8/8/8/8 w - - 0 1", answer: "e8=Q+", desc: "Promote the pawn to a Queen with check." }
                ]
            }
        ]
    },
    {
        phase: "Phase 2: Elementary Tactics (Classes 13-24)",
        classes: [
            {
                name: "Class 13: Spotting Hanging Pieces",
                items: [
                    { title: "Spot the Hanging Knight", type: "puzzle", content: "2r1k3/8/4n3/3R4/8/8/8/8 w - - 0 1", answer: "Rxe5", desc: "Capture the pinned piece." }
                ]
            },
            {
                name: "Class 14: Checkmate in 1 (Back-Rank)",
                items: [
                    { title: "Back-Rank Checkmate", type: "puzzle", content: "6k1/5ppp/8/8/8/8/8/3R2K1 w - - 0 1", answer: "Rd8#", desc: "Checkmate the king on back rank." }
                ]
            },
            {
                name: "Class 15: Checkmate in 1 (f7 Target)",
                items: [
                    { title: "Checkmate on f7", type: "puzzle", content: "r1bqkbnr/pppp1ppp/2n5/4p3/2B1P3/5Q2/PPPP1PPP/RNB1K1NR w KQkq - 4 4", answer: "Qxf7#", desc: "Deliver checkmate on f7." }
                ]
            },
            {
                name: "Class 16: The Fork (Knight Fork)",
                items: [
                    { title: "Knight Fork", type: "puzzle", content: "4k3/8/8/2qN4/8/8/5K2/8 w - - 0 1", answer: "Nf6+", desc: "Fork king and queen." }
                ]
            },
            {
                name: "Class 17: The Fork (Pawn & Queen)",
                items: [
                    { title: "Pawn Fork", type: "puzzle", content: "8/3r1k2/8/3P4/8/8/5K2/8 w - - 0 1", answer: "d6", desc: "Push pawn to fork." }
                ]
            },
            {
                name: "Class 18: The Pin (Absolute Pin)",
                items: [
                    { title: "Queen Absolute Pin", type: "puzzle", content: "4k3/8/8/3q4/8/1B6/5K2/8 w - - 0 1", answer: "Bxd5", desc: "Exploit the absolute pin." }
                ]
            },
            {
                name: "Class 19: The Pin (Relative Pin)",
                items: [
                    { title: "Relative Pin Attack", type: "puzzle", content: "4r1k1/8/8/3b4/8/5R2/5K2/8 w - - 0 1", answer: "Rxf3", desc: "Exploit relative pinning line." }
                ]
            },
            {
                name: "Class 20: The Skewer",
                items: [
                    { title: "Rook Skewer", type: "puzzle", content: "8/8/k7/8/3R2q1/8/5K2/8 w - - 0 1", answer: "Rxg4", desc: "Skewer king and queen." }
                ]
            },
            {
                name: "Class 21: Double Attack",
                items: [
                    { title: "Rook Double Attack", type: "puzzle", content: "8/2k5/8/8/3R2b1/8/8/8 w - - 0 1", answer: "Rxg4", desc: "Double attack Bishop and King." }
                ]
            },
            {
                name: "Class 22: Discovered Attack",
                items: [
                    { title: "Discovered Attack on Rook", type: "puzzle", content: "4k3/8/8/3r4/5B2/8/R7/4K3 w - - 0 1", answer: "Bxd6", desc: "Discover attack on rook." }
                ]
            },
            {
                name: "Class 23: Double Check",
                items: [
                    { title: "Double Check Mate", type: "puzzle", content: "3k4/8/8/8/3N4/8/3R4/3K4 w - - 0 1", answer: "Ne6#", desc: "Double check checkmate." }
                ]
            },
            {
                name: "Class 24: Tactics Test",
                items: [
                    { title: "Spot the Tactic", type: "puzzle", content: "4k3/8/8/2qN4/8/8/8/3K4 w - - 0 1", answer: "Nf6+", desc: "Find the tactical strike." }
                ]
            }
        ]
    },
    {
        phase: "Phase 3: Intermediate Tactics & Endgames (Classes 25-36)",
        classes: [
            {
                name: "Class 25: Checkmate in 2",
                items: [
                    { title: "Mate in 2", type: "puzzle", content: "6k1/5ppp/8/8/8/8/5R2/6K1 w - - 0 1", answer: "Rf8+ Kxf8", desc: "Anastasia checkmate pattern." }
                ]
            },
            {
                name: "Class 26: Checkmate in 2 (Escapes)",
                items: [
                    { title: "Back-Rank Mate in 2", type: "puzzle", content: "6k1/5ppp/8/8/8/8/8/3R2K1 w - - 0 1", answer: "Rd8#", desc: "Calculate back rank mate." }
                ]
            },
            {
                name: "Class 27: Removing the Defender",
                items: [
                    { title: "Remove the Guard", type: "puzzle", content: "4k3/8/2r1b3/3R4/8/8/8/3K4 w - - 0 1", answer: "Rxe5", desc: "Eliminate defender of Bishop." }
                ]
            },
            {
                name: "Class 28: Deflection Tactics",
                items: [
                    { title: "Deflection Sacrifice", type: "puzzle", content: "4k3/8/8/8/8/8/q7/4R2K w - - 0 1", answer: "Re8+ Kxf8", desc: "Deflect king away." }
                ]
            },
            {
                name: "Class 29: Attraction Tactics",
                items: [
                    { title: "Attraction to Fork", type: "puzzle", content: "3k4/8/8/8/3N4/8/8/3K4 w - - 0 1", answer: "Nc6+", desc: "Lure king to fork square." }
                ]
            },
            {
                name: "Class 30: Clearance Sacrifice",
                items: [
                    { title: "Clear the Path", type: "puzzle", content: "8/8/8/8/3B4/8/8/8 w - - 0 1", answer: "Bxa7", desc: "Clear path for promotion." }
                ]
            },
            {
                name: "Class 31: Interference",
                items: [
                    { title: "Interference Attack", type: "puzzle", content: "8/8/8/8/8/8/8/8 w - - 0 1", answer: "h4", desc: "Interfere piece defense." }
                ]
            },
            {
                name: "Class 32: Zugzwang",
                items: [
                    { title: "Zugzwang Trap", type: "puzzle", content: "k7/P7/1K6/8/8/8/8/8 b - - 0 1", answer: "a6", desc: "Force Zugzwang." }
                ]
            },
            {
                name: "Class 33: King & Pawn Endgames",
                items: [
                    { title: "Pawn Promotion Run", type: "puzzle", content: "8/8/P7/8/8/8/k7/4K3 w - - 0 1", answer: "a7", desc: "Calculate pawn race." }
                ]
            },
            {
                name: "Class 34: Rook Endgames",
                items: [
                    { title: "Rook Behind Pawn", type: "puzzle", content: "8/8/1R6/8/8/8/k7/4K3 w - - 0 1", answer: "Rb7", desc: "Place Rook behind pawn." }
                ]
            },
            {
                name: "Class 35: Opening Traps",
                items: [
                    { title: "Legal's Mate Sacrifice", type: "puzzle", content: "r1bqkbnr/ppp2ppp/2np4/4p3/2B1P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 0 4", answer: "Nxe5", desc: "Opening trap exercise." }
                ]
            },
            {
                name: "Class 36: Final Test",
                items: [
                    { title: "Grand Finale", type: "puzzle", content: "4k3/8/8/8/8/8/8/3K4 w - - 0 1", answer: "Kd2", desc: "Grand finale master move." }
                ]
            }
        ]
    }
];

export default function LibraryManager({ userProfile, activeClassId, syncToLiveBoard }) {
    const role = userProfile?.role || 'COACH'; 
    const userEmail = (userProfile?.email || userProfile?.id || '').toLowerCase();

    const [currentPath, setCurrentPath] = useState([{ id: 'root', name: 'Library Home' }]);
    const [folders, setFolders] = useState([]);
    const [items, setItems] = useState([]);
    const [allStudents, setAllStudents] = useState([]); 
    const [allGlobalFolders, setAllGlobalFolders] = useState([]); 
    
    const [contextMenu, setContextMenu] = useState(null);
    const [draggedData, setDraggedData] = useState(null);
    const [dragOverFolderId, setDragOverFolderId] = useState(null);
    const [clipboard, setClipboard] = useState(null); 

    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadPgnData, setUploadPgnData] = useState('');
    const [uploadTargetFolder, setUploadTargetFolder] = useState(''); 

    const [showCreateItemModal, setShowCreateItemModal] = useState(false);
    const [itemForm, setItemForm] = useState({ title: '', fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1', answer: '', points: 10, type: 'puzzle', targetFolderId: '' });
    const [setupBoard, setSetupBoard] = useState({});
    const [setupTurn, setSetupTurn] = useState('w');
    const [setupCastling, setSetupCastling] = useState({ K: true, Q: true, k: true, q: true });
    const [setupTool, setSetupTool] = useState('wK');
    const [setupMode, setSetupMode] = useState('place'); 
    const [setupSelectedSq, setSetupSelectedSq] = useState(null);
    const isDraggingRef = useRef(false);
    const dragInfo = useRef({
        piece: null,
        sourceSq: '',
        floatingEl: null,
        hasMoved: false,
        startX: 0,
        startY: 0
    });

    const [selectedItems, setSelectedItems] = useState([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignTitle, setAssignTitle] = useState('');
    const [assignDueDate, setAssignDueDate] = useState('');
    const [assignTargetStudents, setAssignTargetStudents] = useState([]);
    const [studentSearch, setStudentSearch] = useState('');
    const [previewItem, setPreviewItem] = useState(null);

    const currentFolder = currentPath[currentPath.length - 1];
    const rootFolders = [
        { id: 'public', name: '🌍 Public Library', type: 'public', desc: 'Open for all coaches to use.' },
        { id: 'classwork', name: '📚 Classwork', type: 'classwork', desc: 'Curriculum & Syllabuses.' },
        { id: 'homework', name: '📝 Homework Bank', type: 'homework', desc: 'Pre-made assignments.' },
        { id: 'private_' + userEmail, name: '🔒 My Private Workspace', type: 'private', desc: 'Only visible to you.' }
    ];

    const canEdit = () => {
        if (role === 'OWNER' || role === 'ADMIN') return true;
        if (role === 'COACH') {
            if (currentFolder.id.startsWith('private_') && currentFolder.id === 'private_' + userEmail) return true;
            if (currentFolder.type === 'private' && currentFolder.owner === userEmail) return true;
            return false;
        }
        return false; 
    };

    const getWritableFolders = () => {
        let list = [];
        if (role === 'OWNER' || role === 'ADMIN') {
            list.push({ id: 'public', name: '🌍 Public Library' });
            list.push({ id: 'classwork', name: '📚 Classwork' });
            list.push({ id: 'homework', name: '📝 Homework Bank' });
        }
        list.push({ id: 'private_' + userEmail, name: '🔒 My Private Workspace' });
        
        allGlobalFolders.forEach(f => {
            if (role === 'OWNER' || role === 'ADMIN') {
                list.push({ id: f.id, name: `📁 ${f.name}` });
            } else if (role === 'COACH') {
                if (f.owner === userEmail && f.type === 'private') {
                    list.push({ id: f.id, name: `📁 ${f.name}` });
                }
            }
        });
        return list;
    };

    useEffect(() => {
        const handleClickOutside = () => setContextMenu(null);
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    useEffect(() => {
        const unsub = onSnapshot(collection(db, "users"), snap => { const list = []; snap.forEach(d => { const data = d.data(); if (data.role === 'STUDENT') list.push(data); }); setAllStudents(list); });
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!userEmail) return;
        
        const qOwned = query(collection(db, "library_folders"), where("owner", "==", userEmail));
        const qNonPrivate = query(collection(db, "library_folders"), where("type", "in", ["public", "classwork", "homework"]));
        
        let owned = [];
        let nonPrivate = [];
        
        const combine = (ownedList, nonPrivateList) => {
            const map = {};
            ownedList.forEach(f => { map[f.id] = f; });
            nonPrivateList.forEach(f => { map[f.id] = f; });
            setAllGlobalFolders(Object.values(map));
        };
        
        const unsubOwned = onSnapshot(qOwned, snap => {
            const list = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            owned = list;
            combine(owned, nonPrivate);
        }, err => {
            console.error("Error loading owned folders:", err);
        });
        
        const unsubNonPrivate = onSnapshot(qNonPrivate, snap => {
            const list = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            nonPrivate = list;
            combine(owned, nonPrivate);
        }, err => {
            console.error("Error loading non-private folders:", err);
        });
        
        return () => { unsubOwned(); unsubNonPrivate(); };
    }, [userEmail]);

    useEffect(() => {
        if (currentFolder.id === 'root') return;
        
        const isPrivate = currentFolder.id.startsWith('private_') || currentFolder.type === 'private';
        
        const folderQ = isPrivate
            ? query(collection(db, "library_folders"), where("parentId", "==", currentFolder.id), where("owner", "==", userEmail))
            : query(collection(db, "library_folders"), where("parentId", "==", currentFolder.id));
            
        const itemQ = isPrivate
            ? query(collection(db, "library_items"), where("folderId", "==", currentFolder.id), where("owner", "==", userEmail))
            : query(collection(db, "library_items"), where("folderId", "==", currentFolder.id));
        
        const unsubFolders = onSnapshot(folderQ, snap => { 
            const list = []; 
            snap.forEach(d => {
                const data = d.data();
                list.push({ id: d.id, ...data }); 
            }); 
            setFolders(list.sort((a,b) => a.name.localeCompare(b.name))); 
        }, err => {
            console.error("Error loading folders:", err);
            setFolders([]);
        });
        
        const unsubItems = onSnapshot(itemQ, snap => { 
            const list = []; 
            snap.forEach(d => {
                const data = d.data();
                list.push({ id: d.id, ...data }); 
            }); 
            setItems(list.sort((a,b) => a.title.localeCompare(b.title))); 
        }, err => {
            console.error("Error loading items:", err);
            setItems([]);
        });
        
        setSelectedItems([]); 
        return () => { unsubFolders(); unsubItems(); };
    }, [currentFolder.id, role]);

    const handleContextMenu = (e, item, isFolder) => { 
        e.preventDefault(); 
        const container = e.currentTarget.closest('.library-manager-wrapper');
        let x = e.clientX;
        let y = e.clientY;
        if (container) {
            const rect = container.getBoundingClientRect();
            x = e.clientX - rect.left;
            y = e.clientY - rect.top;
        }
        setContextMenu({ x, y, item, isFolder }); 
    };
    const handleDragStart = (e, item, isFolder) => { if (!canEdit()) return; setDraggedData({ item, isFolder }); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", item.id); };
    const handleDragOver = (e, folderId) => { e.preventDefault(); if (draggedData && draggedData.item.id !== folderId) setDragOverFolderId(folderId); };
    const handleDragLeave = () => setDragOverFolderId(null);

    const handleDrop = async (e, targetFolderId) => {
        e.preventDefault(); setDragOverFolderId(null); if (!draggedData || !canEdit()) return;
        const { item, isFolder } = draggedData; if (isFolder && item.id === targetFolderId) return; 
        const loading = toast.loading("Moving...");
        try {
            if (isFolder) await updateDoc(doc(db, "library_folders", item.id), { parentId: targetFolderId });
            else await updateDoc(doc(db, "library_items", item.id), { folderId: targetFolderId });
            toast.success("Moved successfully!", { id: loading });
        } catch (err) { toast.error("Failed to move.", { id: loading }); }
        setDraggedData(null);
    };

    const handleCopy = (data, isFolder) => { setClipboard({ type: 'copy', data, isFolder }); toast.success(`${isFolder?'Folder':'Item'} Copied!`); setContextMenu(null); };
    const handleCut = (data, isFolder) => { setClipboard({ type: 'cut', data, isFolder }); toast.success(`${isFolder?'Folder':'Item'} Cut (Ready to paste)`); setContextMenu(null); };
    const handlePaste = async () => {
        if (!canEdit()) return toast.error("You don't have permission to write to this folder!");
        if (!clipboard) return; const loading = toast.loading("Pasting...");
        try {
            if (clipboard.isFolder) {
                if (clipboard.type === 'cut') { await updateDoc(doc(db, "library_folders", clipboard.data.id), { parentId: currentFolder.id }); setClipboard(null); toast.success("Folder Moved!", { id: loading }); } 
                else { toast.error("Cannot copy entire folders yet. Use Cut instead.", { id: loading }); }
            } else {
                const { id, ...itemData } = clipboard.data; itemData.folderId = currentFolder.id; itemData.createdAt = Date.now();
                await addDoc(collection(db, "library_items"), itemData);
                if (clipboard.type === 'cut') { await deleteDoc(doc(db, "library_items", clipboard.data.id)); setClipboard(null); }
                toast.success("Item Pasted!", { id: loading });
            }
        } catch (e) { toast.error("Paste failed.", { id: loading }); }
    };

    // 🔥 BULK DELETE LOGIC ADDED HERE
    const handleBulkDelete = async () => {
        if (window.confirm(`Are you sure you want to delete ${selectedItems.length} selected items permanently?`)) {
            const loadingId = toast.loading(`Deleting ${selectedItems.length} items...`);
            try {
                for (const id of selectedItems) {
                    await deleteDoc(doc(db, "library_items", id));
                }
                toast.success("All selected items deleted!", { id: loadingId });
                setSelectedItems([]);
            } catch(e) {
                toast.error("Failed to delete some items.", { id: loadingId });
            }
        }
    };

    const handleCreateFolder = async (e) => { 
        e.preventDefault(); 
        if (!newFolderName.trim()) return; 
        try { 
            const type = currentFolder.type || currentFolder.id.split('_')[0];
            const folderData = { 
                name: newFolderName.trim(), 
                parentId: currentFolder.id, 
                owner: userEmail, 
                type: type, 
                createdAt: serverTimestamp() 
            };
            if (type === 'public' || type === 'classwork' || type === 'homework') {
                folderData.published = true;
            }
            await addDoc(collection(db, "library_folders"), folderData); 
            setNewFolderName(''); 
            setIsCreatingFolder(false); 
            toast.success("Folder Created!"); 
        } catch (err) { 
            toast.error("Error creating folder"); 
        } 
    };
    const handleRenameFolder = async (folder) => { const newName = window.prompt("Enter new folder name:", folder.name); if (newName && newName.trim() !== "" && newName !== folder.name) { await updateDoc(doc(db, "library_folders", folder.id), { name: newName.trim() }); toast.success("Folder renamed!"); setContextMenu(null); } };
    const handleDeleteFolder = async (folderId) => { if (window.confirm("Delete this folder?")) { await deleteDoc(doc(db, "library_folders", folderId)); toast.success("Folder deleted!"); setContextMenu(null); } };
    const handleRenameItem = async (item) => { const newTitle = window.prompt("Enter new file name:", item.title); if (newTitle && newTitle.trim() !== "" && newTitle !== item.title) { await updateDoc(doc(db, "library_items", item.id), { title: newTitle.trim() }); toast.success("File renamed!"); setContextMenu(null); } };
    const handleDeleteItem = async (itemId) => { if(window.confirm("Delete this file?")) { await deleteDoc(doc(db, "library_items", itemId)); setContextMenu(null); } };

    const parsePGNText = (text) => {
        const gameRegex = /((?:\[[A-Za-z]+\s+".*?"\]\s*\r?\n?)+)([^]*?)(?=(?:\[[A-Za-z]+\s+".*?"\])|$)/g;
        const games = [];
        let match;
        while ((match = gameRegex.exec(text)) !== null) {
            const tagText = match[1];
            const moveText = match[2].trim();
            const tags = {};
            const tagRegex = /\[([A-Za-z]+)\s+"(.*?)"\]/g;
            let tagMatch;
            while ((tagMatch = tagRegex.exec(tagText)) !== null) {
                tags[tagMatch[1]] = tagMatch[2];
            }
            if (Object.keys(tags).length > 0 || moveText.length > 0) {
                games.push({ tags, moveText });
            }
        }
        if (games.length === 0 && text.trim().length > 0) {
            if (text.includes('[') && text.includes(']')) {
                const eventSplit = text.split(/\[Event /);
                eventSplit.forEach(part => {
                    if (!part.trim()) return;
                    const fullPart = '[Event ' + part;
                    const innerMatch = /((?:\[[A-Za-z]+\s+".*?"\]\s*\r?\n?)+)([^]*)/.exec(fullPart);
                    if (innerMatch) {
                        const tagText = innerMatch[1];
                        const moveText = innerMatch[2].trim();
                        const tags = {};
                        const tagRegex = /\[([A-Za-z]+)\s+"(.*?)"\]/g;
                        let tagMatch;
                        while ((tagMatch = tagRegex.exec(tagText)) !== null) {
                            tags[tagMatch[1]] = tagMatch[2];
                        }
                        games.push({ tags, moveText });
                    }
                });
            } else {
                games.push({ tags: {}, moveText: text.trim() });
            }
        }
        return games;
    };

    const cleanMoves = (moveText) => {
        let clean = moveText.replace(/\{[^}]*\}/g, '');
        clean = clean.replace(/;.*$/gm, '');
        let prevLength;
        do {
            prevLength = clean.length;
            clean = clean.replace(/\([^()]*\)/g, '');
        } while (clean.length !== prevLength);
        clean = clean.replace(/\d+\s*\.{1,3}/g, '');
        clean = clean.replace(/\$\d+/g, '');
        clean = clean.replace(/(?:1-0|0-1|1\/2-1\/2|\*)/g, '');
        clean = clean.trim().replace(/\s+/g, ' ');
        return clean;
    };

    const extractCommentsFromPgn = (moveText) => {
        const comments = [];
        const commentRegex = /\{([^}]+)\}/g;
        let match;
        while ((match = commentRegex.exec(moveText)) !== null) {
            let text = match[1].replace(/\[%[^\]]+\]/g, '');
            text = text.replace(/\|(\s*\|)+/g, '|').replace(/^\s*\|\s*|\s*\|\s*$/g, '').replace(/\s+/g, ' ').trim();
            if (text) {
                comments.push(text);
            }
        }
        return comments.join(' | ');
    };

    const handlePgnFileChange = (e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onload = (evt) => setUploadPgnData(evt.target.result); reader.readAsText(file); } };
    const handleBulkUpload = async (e) => {
        e.preventDefault();
        if (!uploadPgnData.trim()) return;
        toast.loading("Starting PGN parse...", { id: 'upload' });
        try {
            const games = parsePGNText(uploadPgnData);
            if (games.length === 0) {
                toast.error("No games or exercises found in PGN.", { id: 'upload' });
                return;
            }
            toast.loading(`Importing ${games.length} games...`, { id: 'upload' });
            
            const targetFolder = uploadTargetFolder || currentFolder.id;
            const targetF = allGlobalFolders.find(f => f.id === targetFolder);
            const targetType = targetFolder === 'public' ? 'public' : (targetF?.type || currentFolder.type);
            
            let count = 0;
            const batchSize = 200;
            for (let i = 0; i < games.length; i += batchSize) {
                const chunk = games.slice(i, i + batchSize);
                const batch = writeBatch(db);
                
                chunk.forEach((g, index) => {
                    const setup = g.tags.SetUp === "1" || g.tags.SetUp === 1 || g.tags.FEN;
                    const fen = g.tags.FEN || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
                    const moves = cleanMoves(g.moveText);
                    
                    let title = g.tags.Event || g.tags.White ? `${g.tags.White || ''} vs ${g.tags.Black || ''}`.trim() : `Game Record #${i + index + 1}`;
                    if (title === "vs" || !title) title = `Game Record #${i + index + 1}`;
                    
                    const itemType = setup ? 'puzzle' : 'pgn';
                    
                    let tagsStr = '';
                    Object.entries(g.tags).forEach(([k, v]) => {
                        tagsStr += `[${k} "${v}"]\n`;
                    });
                    const fullPgn = `${tagsStr}\n${g.moveText}`;
                    
                    const itemRef = doc(collection(db, "library_items"));
                    const itemPayload = {
                        folderId: targetFolder,
                        title: title,
                        type: itemType,
                        content: itemType === 'puzzle' ? fen : fullPgn,
                        answer: moves,
                        points: setup ? 10 : 0,
                        owner: userEmail,
                        comments: extractCommentsFromPgn(g.moveText),
                        createdAt: Date.now() + i + index
                    };
                    if (targetType === 'public' || targetType === 'classwork' || targetType === 'homework') {
                        itemPayload.published = true;
                    }
                    
                    batch.set(itemRef, itemPayload);
                    count++;
                });
                await batch.commit();
                toast.loading(`Uploaded ${count} of ${games.length} exercises...`, { id: 'upload' });
            }
            setShowUploadModal(false);
            setUploadPgnData('');
            toast.success(`Successfully uploaded ${count} items!`, { id: 'upload' });
        } catch (err) {
            console.error("Bulk upload error:", err);
            toast.error(`Failed to parse and upload PGN: ${err.message || err}`, { id: 'upload' });
        }
    };

    const handleSeedCurriculum = async () => {
        if (!window.confirm("Are you sure you want to load the pre-made 36-Class Chess Curriculum into the current folder? This will create 3 Phase folders and 36 Class folders containing exercises.")) return;
        const loadingId = toast.loading("Creating Phase folders...");
        try {
            const targetFolder = currentFolder.id;
            for (const phaseInfo of CURRICULUM) {
                // 1. Create Phase Folder
                const phaseFolderRef = await addDoc(collection(db, "library_folders"), {
                    name: phaseInfo.phase,
                    parentId: targetFolder,
                    owner: userEmail,
                    type: currentFolder.type || currentFolder.id.split('_')[0],
                    createdAt: serverTimestamp()
                });
                
                toast.loading(`Creating classes for ${phaseInfo.phase}...`, { id: loadingId });
                
                // 2. Create Class folders under this Phase
                for (const classInfo of phaseInfo.classes) {
                    const classFolderRef = await addDoc(collection(db, "library_folders"), {
                        name: classInfo.name,
                        parentId: phaseFolderRef.id,
                        owner: userEmail,
                        type: currentFolder.type || currentFolder.id.split('_')[0],
                        createdAt: serverTimestamp()
                    });
                    
                    // 3. Create items in this Class folder using a batch
                    const batch = writeBatch(db);
                    classInfo.items.forEach((item, index) => {
                        const itemRef = doc(collection(db, "library_items"));
                        batch.set(itemRef, {
                            folderId: classFolderRef.id,
                            title: item.title,
                            type: item.type,
                            content: item.content,
                            answer: item.answer,
                            points: 10,
                            owner: userEmail,
                            createdAt: Date.now() + index
                        });
                    });
                    await batch.commit();
                }
            }
            toast.success("Successfully loaded 36-Class Chess Curriculum! 🎓", { id: loadingId });
        } catch (err) {
            console.error("Curriculum seeding error:", err);
            toast.error("Failed to seed curriculum.", { id: loadingId });
        }
    };

    const handlePublishAll = async () => {
        const loading = toast.loading("Publishing all public drafts...");
        try {
            const batch = writeBatch(db);
            let count = 0;

            const foldersQ = query(collection(db, "library_folders"), where("type", "==", "public"), where("published", "==", false));
            const foldersSnap = await getDocs(foldersQ);
            foldersSnap.forEach(d => {
                batch.update(doc(db, "library_folders", d.id), { published: true });
                count++;
            });

            const itemsQ = query(collection(db, "library_items"), where("published", "==", false));
            const itemsSnap = await getDocs(itemsQ);
            itemsSnap.forEach(d => {
                batch.update(doc(db, "library_items", d.id), { published: true });
                count++;
            });

            if (count > 0) {
                await batch.commit();
                toast.success(`Successfully published ${count} draft(s) across the Public Library! 🚀`, { id: loading });
            } else {
                toast.success("No drafts found to publish!", { id: loading });
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to publish all drafts.", { id: loading });
        }
    };

    const handlePublishCurrentFolder = async () => {
        const loading = toast.loading("Publishing current folder drafts...");
        try {
            const batch = writeBatch(db);
            let count = 0;
            
            if (currentFolder.id !== 'public' && currentFolder.published === false) {
                batch.update(doc(db, "library_folders", currentFolder.id), { published: true });
                count++;
            }
            
            folders.forEach(f => {
                if (f.published === false) {
                    batch.update(doc(db, "library_folders", f.id), { published: true });
                    count++;
                }
            });
            
            items.forEach(item => {
                if (item.published === false) {
                    batch.update(doc(db, "library_items", item.id), { published: true });
                    count++;
                }
            });
            
            if (count > 0) {
                await batch.commit();
                toast.success(`Published ${count} draft(s) in this folder! 🚀`, { id: loading });
            } else {
                toast.success("No drafts found in this folder!", { id: loading });
            }
        } catch (e) {
            toast.error("Failed to publish folder drafts.", { id: loading });
        }
    };

    const updateFenFromBoard = (board, turn, castleInfo) => {
        let fen = '';
        for (let r = 8; r >= 1; r--) {
            let empty = 0;
            for (let c = 0; c < 8; c++) { const sq = `${String.fromCharCode(97 + c)}${r}`; const p = board[sq]; if (p) { if (empty > 0) { fen += empty; empty = 0; } fen += p[0] === 'w' ? p[1].toUpperCase() : p[1].toLowerCase(); } else { empty++; } }
            if (empty > 0) fen += empty; if (r > 1) fen += '/';
        }
        let castlingStr = '';
        if(castleInfo.K) castlingStr += 'K'; if(castleInfo.Q) castlingStr += 'Q'; if(castleInfo.k) castlingStr += 'k'; if(castleInfo.q) castlingStr += 'q';
        if(!castlingStr) castlingStr = '-';
        setItemForm(prev => ({...prev, fen: `${fen} ${turn} ${castlingStr} - 0 1`}));
    };

    const parseFenToBoard = (fenStr) => {
        try {
            const board = {}; const parts = fenStr.split(' '); const position = parts[0]; const turn = parts[1] || 'w'; const castle = parts[2] || '-';
            let r = 8;
            position.split('/').forEach(row => { let c = 0; for (let char of row) { if (!isNaN(char)) { c += parseInt(char); } else { const sq = `${String.fromCharCode(97 + c)}${r}`; const color = char === char.toUpperCase() ? 'w' : 'b'; board[sq] = color + char.toUpperCase(); c++; } } r--; });
            setSetupBoard(board); if (turn === 'w' || turn === 'b') setSetupTurn(turn);
            setSetupCastling({ K: castle.includes('K'), Q: castle.includes('Q'), k: castle.includes('k'), q: castle.includes('q') });
        } catch(e) {}
    };

    const handlePieceDragStart = (e, piece, sourceSq = '') => {
        if (e.button !== undefined && e.button !== 0) return; // Left click only
        if (setupMode === 'play') return;

        const floating = document.createElement('img');
        floating.src = PIECE_IMAGES[piece];
        floating.style.position = 'fixed';
        floating.style.width = '45px';
        floating.style.height = '45px';
        floating.style.pointerEvents = 'none';
        floating.style.zIndex = '10000';
        floating.style.left = `${e.clientX - 22}px`;
        floating.style.top = `${e.clientY - 22}px`;
        floating.style.opacity = '0.85';
        floating.style.transform = 'scale(1.15)';
        document.body.appendChild(floating);

        dragInfo.current = {
            piece,
            sourceSq,
            floatingEl: floating,
            hasMoved: false,
            startX: e.clientX,
            startY: e.clientY
        };

        document.addEventListener('pointermove', onPiecePointerMove);
        document.addEventListener('pointerup', onPiecePointerUp);
    };

    const onPiecePointerMove = (e) => {
        const info = dragInfo.current;
        if (!info.floatingEl) return;

        info.floatingEl.style.left = `${e.clientX - 22}px`;
        info.floatingEl.style.top = `${e.clientY - 22}px`;

        if (Math.abs(e.clientX - info.startX) > 5 || Math.abs(e.clientY - info.startY) > 5) {
            info.hasMoved = true;
        }
    };

    const onPiecePointerUp = (e) => {
        document.removeEventListener('pointermove', onPiecePointerMove);
        document.removeEventListener('pointerup', onPiecePointerUp);

        const { piece, sourceSq, floatingEl, hasMoved } = dragInfo.current;
        if (floatingEl) {
            floatingEl.remove();
        }

        if (hasMoved && piece) {
            isDraggingRef.current = true;
            setTimeout(() => {
                isDraggingRef.current = false;
            }, 50);

            const element = document.elementFromPoint(e.clientX, e.clientY);
            const squareEl = element?.closest('[data-square]');
            const trashEl = element?.closest('[data-trash]');

            const newBoard = { ...setupBoard };

            if (trashEl) {
                if (sourceSq) {
                    delete newBoard[sourceSq];
                    setSetupBoard(newBoard);
                    updateFenFromBoard(newBoard, setupTurn, setupCastling);
                    toast.success("Piece removed!");
                }
            } else if (squareEl) {
                const targetSq = squareEl.getAttribute('data-square');
                if (sourceSq) {
                    delete newBoard[sourceSq];
                }
                newBoard[targetSq] = piece;
                setSetupBoard(newBoard);
                updateFenFromBoard(newBoard, setupTurn, setupCastling);
            }
        }

        dragInfo.current = { piece: null, sourceSq: '', floatingEl: null, hasMoved: false, startX: 0, startY: 0 };
    };

    const handleBoardClick = (sq) => {
        if (isDraggingRef.current) return;
        if (setupMode === 'play') {
            if (setupSelectedSq) {
                const temp = new Chess();
                try {
                    temp.load(itemForm.fen);
                    const move = temp.move({ from: setupSelectedSq, to: sq, promotion: 'q' });
                    if (move) {
                        // Update answer string
                        const newAns = itemForm.answer ? `${itemForm.answer}, ${move.san}` : move.san;
                        // Update FEN to reflect the new position
                        const newFen = temp.fen();
                        setItemForm(prev => ({...prev, answer: newAns, fen: newFen}));
                        // Update visual board state so pieces move
                        const newBoard = {};
                        temp.board().forEach((row, rIdx) => row.forEach((cell, cIdx) => {
                            if (cell) {
                                const col = String.fromCharCode(97 + cIdx);
                                const row2 = 8 - rIdx;
                                newBoard[`${col}${row2}`] = cell.color + cell.type.toUpperCase();
                            }
                        }));
                        setSetupBoard(newBoard);
                        // Flip turn
                        setSetupTurn(temp.turn());
                        toast.success(`✅ Added: ${move.san}`);
                    } else {
                        toast.error("Invalid move!");
                    }
                } catch(e) { toast.error("Invalid position for engine."); }
                setSetupSelectedSq(null);
            } else {
                // Select a piece to move - only allow current turn's pieces
                const p = setupBoard[sq];
                const currentTurn = itemForm.fen.split(' ')[1] || setupTurn;
                if (p && p[0] === currentTurn) setSetupSelectedSq(sq);
                else if (p && p[0] !== currentTurn) toast.error(`It's ${currentTurn === 'w' ? 'White' : 'Black'}'s turn!`);
            }
        } else {
            const nb = {...setupBoard}; if (setupTool === 'eraser') delete nb[sq]; else nb[sq] = setupTool; 
            setSetupBoard(nb); updateFenFromBoard(nb, setupTurn, setupCastling); 
        }
    };

    const toggleCastling = (type) => { const newCastle = { ...setupCastling, [type]: !setupCastling[type] }; setSetupCastling(newCastle); updateFenFromBoard(setupBoard, setupTurn, newCastle); };

    const handleCreateItem = async (e) => {
        e.preventDefault(); if (!itemForm.title.trim() || !itemForm.fen.trim()) return toast.error("Title and FEN are required!");
        try {
            const targetFolder = itemForm.targetFolderId || currentFolder.id;
            const targetF = allGlobalFolders.find(f => f.id === targetFolder);
            const targetType = targetFolder === 'public' ? 'public' : (targetF?.type || currentFolder.type);
            
            const itemData = { 
                folderId: targetFolder, 
                title: itemForm.title.trim(), 
                type: itemForm.type, 
                content: itemForm.fen.trim(), 
                answer: itemForm.answer.trim(), 
                points: Number(itemForm.points) || 10, 
                owner: userEmail, 
                createdAt: Date.now() 
            };
            if (targetType === 'public' || targetType === 'classwork' || targetType === 'homework') {
                itemData.published = true;
            }
            
            await addDoc(collection(db, "library_items"), itemData);
            setShowCreateItemModal(false); setItemForm({ title: '', fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1', answer: '', points: 10, type: 'puzzle', targetFolderId: '' }); parseFenToBoard('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'); toast.success("Item saved to selected folder!");
        } catch (err) { toast.error("Error creating item"); }
    };

    const handleSelectAll = () => { if (selectedItems.length === items.length) setSelectedItems([]); else setSelectedItems(items.map(i => i.id)); };
    const toggleSelectItem = (id) => { if (selectedItems.includes(id)) setSelectedItems(selectedItems.filter(i => i !== id)); else setSelectedItems([...selectedItems, id]); };
    const handleSingleAssign = (id) => { setSelectedItems([id]); setShowAssignModal(true); setContextMenu(null); };

    const submitHomework = async (e) => {
        e.preventDefault();
        if (assignTargetStudents.length === 0) return toast.error("Select at least one student!");
        if (!assignTitle.trim()) return toast.error("Title is required!");
        const loading = toast.loading("Assigning...");
        try {
            const materials = items.filter(i => selectedItems.includes(i.id)).map(i => ({ id: i.id, title: i.title, type: i.type, content: i.content, answer: i.answer || '', points: i.points || 10 }));
            await addDoc(collection(db, "homeworks"), { title: assignTitle.trim(), dueDate: assignDueDate, assignedBy: userEmail, assignedTo: assignTargetStudents, materials: materials, completedBy: [], createdAt: new Date().toISOString() });
            toast.success(`Assigned to ${assignTargetStudents.length} student(s)!`, { id: loading });
            setShowAssignModal(false); setAssignTitle(''); setAssignDueDate(''); setAssignTargetStudents([]); setSelectedItems([]);
        } catch (err) { toast.error("Failed to assign.", { id: loading }); }
    };

    const navigateTo = (folder) => { setCurrentPath([...currentPath, folder]); setContextMenu(null); };
    const navigateUp = (index) => { setCurrentPath(currentPath.slice(0, index + 1)); setContextMenu(null); };
    const inputStyle = { padding: '10px', borderRadius: '6px', background: 'var(--bg-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', color: '#0f172a', width: '100%', boxSizing: 'border-box', fontWeight: 'bold' };

    useEffect(() => { if(showCreateItemModal && Object.keys(setupBoard).length===0) parseFenToBoard(itemForm.fen); }, [showCreateItemModal]);

    return (
        <div className="library-manager-wrapper" style={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', borderRadius: '12px', border: '1px solid rgba(163, 177, 198, 0.4)', overflow: 'hidden', position: 'relative' }}>
            
            {contextMenu && (
                <div style={{position: 'absolute', top: contextMenu.y, left: contextMenu.x, background: 'var(--bg-primary)', border: '1px solid #3b82f6', borderRadius: '8px', padding: '5px', zIndex: 9999, boxShadow: '0 4px 15px rgba(0,0,0,0.15), var(--panel-shadow)', width: '180px', display: 'flex', flexDirection: 'column' }}>
                    <div style={{padding: '8px 10px', borderBottom: '1px solid rgba(163, 177, 198, 0.4)', color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {contextMenu.item.title || contextMenu.item.name}
                    </div>
                    {contextMenu.isFolder ? (
                        <>
                            <button onClick={() => navigateTo(contextMenu.item)} style={{padding: '10px', background: 'transparent', color: 'var(--text-primary)', border: 'none', textAlign: 'left', cursor: 'pointer', borderRadius: '4px', fontWeight: 'bold', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>📂 Open Folder</button>
                            {canEdit() && <button onClick={() => handleCut(contextMenu.item, true)} style={{padding: '10px', background: 'transparent', color: 'var(--text-primary)', border: 'none', textAlign: 'left', cursor: 'pointer', borderRadius: '4px', fontWeight: 'bold', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>✂️ Cut</button>}
                            {canEdit() && <button onClick={() => handleRenameFolder(contextMenu.item)} style={{padding: '10px', background: 'transparent', color: 'var(--text-primary)', border: 'none', textAlign: 'left', cursor: 'pointer', borderRadius: '4px', fontWeight: 'bold', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>✏️ Rename</button>}
                            {canEdit() && <button onClick={() => handleDeleteFolder(contextMenu.item.id)} style={{padding: '10px', background: 'transparent', color: '#ef4444', border: 'none', textAlign: 'left', cursor: 'pointer', borderRadius: '4px', fontWeight: 'bold', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(239,68,68,0.05)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>🗑️ Delete</button>}
                        </>
                    ) : (
                        <>
                            <button onClick={() => { setPreviewItem(contextMenu.item); setContextMenu(null); }} style={{padding: '10px', background: 'transparent', color: 'var(--text-primary)', border: 'none', textAlign: 'left', cursor: 'pointer', borderRadius: '4px', fontWeight: 'bold', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>👁️ Preview / Read PGN</button>
                            {activeClassId && contextMenu.item.type === 'lesson' && (
                                <button onClick={() => {
                                    const playlistItems = items || [];
                                    syncToLiveBoard(contextMenu.item.content, contextMenu.item.type, contextMenu.item, playlistItems);
                                    setContextMenu(null);
                                }} style={{padding: '10px', background: 'transparent', color: '#3b82f6', border: 'none', textAlign: 'left', cursor: 'pointer', borderRadius: '4px', fontWeight: 'bold', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(59,130,246,0.05)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>🎓 Load Lesson to Class</button>
                            )}
                            {activeClassId && contextMenu.item.type !== 'lesson' && (
                                <button onClick={() => {
                                    const playlistItems = items || [];
                                    localStorage.setItem('classroomPlaylist', JSON.stringify(playlistItems));
                                    const itemIndex = playlistItems.findIndex(i => i.id === contextMenu.item.id);
                                    localStorage.setItem('classroomPlaylistIndex', itemIndex >= 0 ? itemIndex.toString() : '0');
                                    syncToLiveBoard(contextMenu.item.content, contextMenu.item.type, contextMenu.item, playlistItems);
                                    setContextMenu(null);
                                }} style={{padding: '10px', background: 'transparent', color: '#ec4899', border: 'none', textAlign: 'left', cursor: 'pointer', borderRadius: '4px', fontWeight: 'bold', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(236,72,153,0.05)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>🚀 Launch to Board</button>
                            )}
                            <button onClick={() => handleSingleAssign(contextMenu.item.id)} style={{padding: '10px', background: 'transparent', color: '#2563eb', border: 'none', textAlign: 'left', cursor: 'pointer', borderRadius: '4px', fontWeight: 'bold', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(37,99,235,0.05)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>📝 Assign HW</button>
                            <div style={{height: '1px', background: 'rgba(163, 177, 198, 0.4)', margin: '5px 0' }}></div>
                            <button onClick={() => handleCopy(contextMenu.item, false)} style={{padding: '10px', background: 'transparent', color: 'var(--text-primary)', border: 'none', textAlign: 'left', cursor: 'pointer', borderRadius: '4px', fontWeight: 'bold', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>📋 Copy</button>
                            {canEdit() && (
                                <>
                                    <button onClick={() => handleCut(contextMenu.item, false)} style={{padding: '10px', background: 'transparent', color: 'var(--text-primary)', border: 'none', textAlign: 'left', cursor: 'pointer', borderRadius: '4px', fontWeight: 'bold', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>✂️ Cut</button>
                                    <button onClick={() => handleRenameItem(contextMenu.item)} style={{padding: '10px', background: 'transparent', color: 'var(--text-primary)', border: 'none', textAlign: 'left', cursor: 'pointer', borderRadius: '4px', fontWeight: 'bold', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>✏️ Rename</button>
                                    <button onClick={() => handleDeleteItem(contextMenu.item.id)} style={{padding: '10px', background: 'transparent', color: '#ef4444', border: 'none', textAlign: 'left', cursor: 'pointer', borderRadius: '4px', fontWeight: 'bold', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = 'rgba(239,68,68,0.05)'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>🗑️ Delete</button>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}

            <div style={{padding: '15px 20px', background: 'var(--bg-primary)', borderBottom: '1px solid rgba(163, 177, 198, 0.4)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{display: 'flex', gap: '8px', alignItems: 'center', color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 'bold', flexWrap: 'wrap' }}>
                    {currentPath.map((crumb, idx) => (
                        <React.Fragment key={crumb.id}>
                            <span 
                                onClick={() => navigateUp(idx)} 
                                onDragOver={(e) => { e.preventDefault(); if(idx > 0) setDragOverFolderId(crumb.id); }}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, crumb.id)}
                                style={{cursor: 'pointer', color: dragOverFolderId === crumb.id ? '#10b981' : (idx === currentPath.length - 1 ? 'var(--text-primary)' : '#2563eb'), transition: '0.2s', padding: '4px 8px', borderRadius: '6px', background: dragOverFolderId === crumb.id ? 'rgba(16, 185, 129, 0.2)' : 'transparent' }}
                            >
                                {crumb.name}
                            </span>
                            {idx < currentPath.length - 1 && <span>/</span>}
                        </React.Fragment>
                    ))}
                </div>

                {currentFolder.id !== 'root' && canEdit() && (
                    <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {clipboard && ( <button onClick={handlePaste} style={{padding: '8px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', animation: 'pulse 2s infinite' }}>📋 Paste ({clipboard.type})</button> )}
                        <button onClick={() => setIsCreatingFolder(true)} style={{padding: '8px 12px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', boxShadow: 'var(--neu-shadow)' }} onMouseOver={e => e.currentTarget.style.boxShadow = 'var(--neu-shadow-hover)'} onMouseOut={e => e.currentTarget.style.boxShadow = 'var(--neu-shadow)'}>📁 New Folder</button>
                        <button onClick={() => { setSetupBoard({}); setItemForm({...itemForm, targetFolderId: currentFolder.id, fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'}); setShowCreateItemModal(true); }} style={{padding: '8px 12px', background: '#f59e0b', color: 'black', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', boxShadow: 'var(--neu-shadow)' }} onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'} onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}>➕ New Item</button>
                        <button onClick={() => { setUploadTargetFolder(currentFolder.id); setShowUploadModal(true); }} style={{padding: '8px 12px', background: '#10b981', color: 'black', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', boxShadow: 'var(--neu-shadow)' }} onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'} onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}>📥 Bulk PGN</button>
                        {(currentFolder.id === 'public' || currentFolder.type === 'public') && (role === 'OWNER' || role === 'ADMIN') && (
                            <button 
                                onClick={currentFolder.id === 'public' ? handlePublishAll : handlePublishCurrentFolder} 
                                style={{
                                    padding: '8px 14px', 
                                    background: 'linear-gradient(135deg, #10b981, #059669)', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '6px', 
                                    cursor: 'pointer', 
                                    fontWeight: 'bold', 
                                    fontSize: '13px', 
                                    boxShadow: '0 4px 10px rgba(16, 185, 129, 0.25)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                🚀 {currentFolder.id === 'public' ? 'Publish All Drafts' : 'Publish Current Folder'}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {isCreatingFolder && (
                <form onSubmit={handleCreateFolder} style={{padding: '15px 20px', background: 'var(--bg-primary)', borderBottom: '1px solid rgba(163, 177, 198, 0.4)', display: 'flex', gap: '10px' }}>
                    <input autoFocus value={newFolderName} onChange={e=>setNewFolderName(e.target.value)} placeholder="Folder Name..." style={{flex: 1, padding: '10px', borderRadius: '6px', background: 'var(--bg-primary)', border: '1px solid #3b82f6', color: 'var(--text-primary)' }} required />
                    <button type="submit" style={{padding: '10px 15px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Create</button>
                    <button type="button" onClick={() => setIsCreatingFolder(false)} style={{padding: '10px 15px', background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
                </form>
            )}

            <div style={{flex: 1, padding: '20px', overflowY: 'auto' }}>
                {currentFolder.id === 'root' ? (
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
                        {rootFolders.map(folder => (
                            <div 
                                key={folder.id} onClick={() => navigateTo(folder)} 
                                onDragOver={(e) => { e.preventDefault(); setDragOverFolderId(folder.id); }}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, folder.id)}
                                style={{ background: dragOverFolderId === folder.id ? 'rgba(59, 130, 246, 0.3)' : 'var(--bg-primary)', padding: '20px', borderRadius: '12px', border: dragOverFolderId === folder.id ? '2px solid #3b82f6' : '1px solid rgba(163, 177, 198, 0.4)', cursor: 'pointer', transition: 'all 0.2s', ':hover': { transform: 'translateY(-5px)' } }}
                            >
                                <h3 style={{margin: '0 0 10px 0', color: 'var(--text-primary)' }}>{folder.name}</h3><p style={{margin: 0, color: 'var(--text-secondary)', fontSize: '13px' }}>{folder.desc}</p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        {folders.length === 0 && items.length === 0 && <div style={{color: '#64748b', textAlign: 'center', marginTop: '50px' }}>This folder is empty. Right-click or use buttons above to add items.</div>}
                        
                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                            {folders.map(f => (
                                <div 
                                    key={f.id} 
                                    draggable={canEdit()}
                                    onDragStart={(e) => handleDragStart(e, f, true)}
                                    onDragOver={(e) => handleDragOver(e, f.id)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, f.id)}
                                    onContextMenu={(e) => handleContextMenu(e, f, true)}
                                    onClick={() => navigateTo(f)}
                                    style={{background: dragOverFolderId === f.id ? 'rgba(16, 185, 129, 0.3)' : 'var(--bg-primary)', padding: '15px', borderRadius: '8px', border: dragOverFolderId === f.id ? '2px solid #10b981' : '1px solid rgba(163, 177, 198, 0.4)', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}
                                >
                                    <span style={{fontSize: '24px', pointerEvents: 'none' }}>📁</span>
                                    <span style={{color: 'var(--text-primary)', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {f.name}
                                        {f.type === 'public' && f.published === false && (
                                            <span style={{ fontSize: '9px', background: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>Draft</span>
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {items.length > 0 && (
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-primary)', padding: '12px 15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #3b82f6' }}>
                                <label style={{display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 'bold' }}>
                                    <input type="checkbox" checked={selectedItems.length === items.length} onChange={handleSelectAll} style={{width: '18px', height: '18px', cursor: 'pointer' }} />
                                    Select All
                                </label>
                                {/* 🔥 NEW: DELETE ALL / BULK DELETE BUTTON */}
                                {selectedItems.length > 0 && (
                                    <div style={{display: 'flex', gap: '10px' }}>
                                        <button onClick={() => setShowAssignModal(true)} style={{padding: '8px 15px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' }}>
                                            📥 Assign {selectedItems.length} as HW
                                        </button>
                                        {canEdit() && (
                                            <button onClick={handleBulkDelete} style={{padding: '8px 15px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' }}>
                                                🗑️ Delete {selectedItems.length}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <div style={{display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {items.map(item => (
                                <div 
                                    key={item.id} 
                                    draggable={canEdit()}
                                    onDragStart={(e) => handleDragStart(e, item, false)}
                                    onContextMenu={(e) => handleContextMenu(e, item, false)}
                                    style={{background: selectedItems.includes(item.id) ? 'rgba(59, 130, 246, 0.1)' : 'var(--bg-primary)', padding: '12px 15px', borderRadius: '8px', border: selectedItems.includes(item.id) ? '1px solid #3b82f6' : '1px solid rgba(163, 177, 198, 0.4)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', transition: 'all 0.2s', cursor: 'grab' }}
                                >
                                    <div style={{display: 'flex', alignItems: 'center', gap: '15px', pointerEvents: 'none' }}>
                                        <input type="checkbox" checked={selectedItems.includes(item.id)} onChange={() => toggleSelectItem(item.id)} style={{width: '18px', height: '18px', cursor: 'pointer', pointerEvents: 'auto' }} />
                                        <span style={{fontSize: '24px' }}>{item.type === 'pgn' ? '📜' : item.type === 'puzzle' ? '🎯' : '♟️'}</span>
                                        <div>
                                            <div style={{color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                {item.title}
                                                {item.type === 'puzzle' && <span style={{background: '#f59e0b', color: 'black', fontSize: '10px', padding: '2px 6px', borderRadius: '4px' }}>Puzzle ({item.points} XP)</span>}
                                                {(currentFolder.type === 'public' || currentFolder.id === 'public') && item.published === false && (
                                                    <span style={{background: '#64748b', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>Draft</span>
                                                )}
                                            </div>
                                            <div style={{color: '#64748b', fontSize: '12px', marginTop: '2px' }}>Added by {item.owner?.split('@')[0]}</div>
                                        </div>
                                    </div>
                                    
                                    <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <button 
                                            onClick={() => setPreviewItem(item)}
                                            style={{ padding: '6px 12px', background: 'rgba(59, 130, 246, 0.1)', color: '#2563eb', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                                        >
                                            👁️ Preview
                                        </button>

                                        {activeClassId && item.type === 'lesson' && (
                                            <button 
                                                onClick={() => {
                                                    const playlistItems = items || [];
                                                    syncToLiveBoard(item.content, item.type, item, playlistItems);
                                                }}
                                                style={{ padding: '6px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)' }}
                                            >
                                                🎓 Load Lesson
                                            </button>
                                        )}

                                        {activeClassId && item.type !== 'lesson' && (
                                            <button 
                                                onClick={() => {
                                                    const playlistItems = items || [];
                                                    localStorage.setItem('classroomPlaylist', JSON.stringify(playlistItems));
                                                    const itemIndex = playlistItems.findIndex(i => i.id === item.id);
                                                    localStorage.setItem('classroomPlaylistIndex', itemIndex >= 0 ? itemIndex.toString() : '0');
                                                    syncToLiveBoard(item.content, item.type, item, playlistItems);
                                                }}
                                                style={{ padding: '6px 12px', background: '#ec4899', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(236, 72, 153, 0.3)' }}
                                            >
                                                🚀 Launch Board
                                            </button>
                                        )}

                                        <button 
                                            onClick={() => handleSingleAssign(item.id)}
                                            style={{ padding: '6px 12px', background: '#f59e0b', color: 'black', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)' }}
                                        >
                                            📝 Assign
                                        </button>

                                        {canEdit() && (
                                            <>
                                                <button 
                                                    onClick={() => handleRenameItem(item)}
                                                    style={{ padding: '6px 10px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid rgba(163, 177, 198, 0.4)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                                                    title="Rename"
                                                >
                                                    ✏️
                                                </button>
                                                <button 
                                                    onClick={() => handleDeleteItem(item.id)}
                                                    style={{ padding: '6px 10px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                                                    title="Delete"
                                                >
                                                    🗑️
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* 🔥 THIS LOADS THE IMMERSIVE PGN READER WHEN CLICKED */}
            {previewItem && (
                <InteractivePGNReader 
                    item={previewItem} 
                    items={items} 
                    onNavigate={(newItem) => setPreviewItem(newItem)} 
                    onAssign={handleSingleAssign}
                    onClose={() => setPreviewItem(null)} 
                    activeClassId={activeClassId}
                    syncToLiveBoard={syncToLiveBoard}
                />
            )}

            {/* ASSIGN MODAL */}
            {showAssignModal && (
                <div style={{position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.85)', zIndex: 10005, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', boxSizing: 'border-box' }}>
                    <form onSubmit={submitHomework} style={{background: 'var(--bg-primary)', width: '100%', maxWidth: '500px', padding: '25px', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '15px', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
                        <h2 style={{margin: 0, color: '#3b82f6', display: 'flex', justifyContent: 'space-between' }}>📝 Assign Homework ({selectedItems.length} items)<span onClick={() => setShowAssignModal(false)} style={{cursor: 'pointer', color: '#ef4444' }}>✖</span></h2>
                        <div><label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Homework Title</label><input type="text" value={assignTitle} onChange={e=>setAssignTitle(e.target.value)} required placeholder="e.g. Weekend Tactical Drills" style={inputStyle} /></div>
                        <div><label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Due Date (Optional)</label><input type="date" value={assignDueDate} onChange={e=>setAssignDueDate(e.target.value)} style={inputStyle} /></div>
                        <div><label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', marginBottom: '5px', display: 'flex', justifyContent: 'space-between' }}>Select Target Students<span style={{color: '#10b981', cursor: 'pointer' }} onClick={() => setAssignTargetStudents(allStudents.map(s => s.email))}>Select All</span></label>
                            <div style={{maxHeight: '200px', overflowY: 'auto', background: 'var(--bg-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', borderRadius: '6px', padding: '10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <input type="text" placeholder="🔍 Search student..." value={studentSearch} onChange={e=>setStudentSearch(e.target.value)} style={{padding: '8px', borderRadius: '4px', border: 'none', background: 'var(--bg-primary)', color: 'var(--text-primary)', marginBottom: '10px' }} />
                                {allStudents.filter(s => (s.name || s.email).toLowerCase().includes(studentSearch.toLowerCase())).map(s => (
                                    <label key={s.email} style={{display: 'flex', alignItems: 'center', gap: '10px', color: 'white', padding: '8px', background: assignTargetStudents.includes(s.email) ? 'rgba(59, 130, 246, 0.2)' : 'transparent', borderRadius: '4px', cursor: 'pointer', transition: 'background 0.2s' }}>
                                        <input type="checkbox" checked={assignTargetStudents.includes(s.email)} onChange={(e) => { if (e.target.checked) setAssignTargetStudents([...assignTargetStudents, s.email]); else setAssignTargetStudents(assignTargetStudents.filter(email => email !== s.email)); }} style={{width: '16px', height: '16px', cursor: 'pointer' }} />
                                        <span>👤 {s.name || s.email.split('@')[0]}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <button type="submit" style={{padding: '15px', background: '#3b82f6', color: 'white', fontSize: '16px', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer', marginTop: '10px' }}>🚀 Dispatch</button>
                    </form>
                </div>
            )}

            {showCreateItemModal && (
                <div style={{position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', boxSizing: 'border-box' }}>
                    <div style={{background: '#e0e5ec', width: '100%', maxWidth: '900px', padding: '25px', borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.45)', display: 'flex', flexDirection: 'column', gap: '20px', maxHeight: '95vh', overflowY: 'auto', boxShadow: '8px 8px 20px rgba(163, 177, 198, 0.2), -8px -8px 20px rgba(255, 255, 255, 0.55)' }}>
                        <h2 style={{margin: 0, color: 'var(--text-primary)', display: 'flex', justifyContent: 'space-between', fontSize: '20px', fontWeight: '800' }}>➕ Create Item (Setup Board)<span onClick={() => setShowCreateItemModal(false)} style={{cursor: 'pointer', color: '#ef4444' }}>✖</span></h2>
                        <div style={{display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                            <div style={{flex: '0 0 auto', width: '320px', maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div style={{display: 'flex', background: 'var(--bg-primary)', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255, 255, 255, 0.45)', boxShadow: 'var(--neu-shadow-inset)' }}>
                                    <button onClick={() => setSetupMode('place')} style={{flex: 1, padding: '12px', fontWeight: 'bold', border: 'none', cursor: 'pointer', background: setupMode === 'place' ? 'var(--btn-hover)' : 'transparent', color: setupMode === 'place' ? '#2563eb' : 'var(--text-secondary)', boxShadow: setupMode === 'place' ? 'var(--neu-shadow-inset)' : 'none', transition: 'all 0.2s' }}>1. 🧩 Place Pieces</button>
                                    <button onClick={() => setSetupMode('play')} style={{flex: 1, padding: '12px', fontWeight: 'bold', border: 'none', cursor: 'pointer', background: setupMode === 'play' ? 'var(--btn-hover)' : 'transparent', color: setupMode === 'play' ? '#10b981' : 'var(--text-secondary)', boxShadow: setupMode === 'play' ? 'var(--neu-shadow-inset)' : 'none', transition: 'all 0.2s' }}>2. 🎮 Play Answer</button>
                                </div>
                                <div style={{display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '4px', background: '#e0e5ec', padding: '8px', borderRadius: '12px', boxShadow: 'var(--neu-shadow-inset)', border: '1px solid rgba(255, 255, 255, 0.45)', opacity: setupMode === 'play' ? 0.5 : 1, pointerEvents: setupMode === 'play' ? 'none' : 'auto' }}>
                                    {['wK','wQ','wR','wB','wN','wP','bK','bQ','bR','bB','bN','bP'].map(p => (
                                        <img 
                                            key={p} 
                                            src={PIECE_IMAGES[p]} 
                                            alt={p} 
                                            onClick={()=>setSetupTool(p)} 
                                            draggable={false}
                                            onPointerDown={(e) => handlePieceDragStart(e, p, '')}
                                            style={{
                                                width:'24px', 
                                                height:'24px', 
                                                cursor:'grab', 
                                                background: setupTool===p ? 'var(--btn-hover)':'transparent', 
                                                borderRadius:'5px',
                                                boxShadow: setupTool===p ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                                                border: '1px solid rgba(255, 255, 255, 0.4)',
                                                transition: 'all 0.15s',
                                                touchAction: 'none'
                                            }}
                                        />
                                    ))}
                                    <div 
                                        onClick={()=>setSetupTool('eraser')} 
                                        data-trash="true"
                                        style={{
                                            width:'24px', 
                                            height:'24px', 
                                            cursor:'pointer', 
                                            background: setupTool==='eraser' ? '#ef4444':'var(--btn-bg)', 
                                            color: setupTool==='eraser' ? 'white' : 'var(--text-primary)', 
                                            borderRadius:'5px', 
                                            display:'flex', 
                                            justifyContent:'center', 
                                            alignItems:'center',
                                            boxShadow: setupTool==='eraser' ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                                            border: '1px solid rgba(255, 255, 255, 0.4)',
                                            transition: 'all 0.15s',
                                            fontSize: '14px'
                                        }}
                                        title="Click to erase, or drag a piece here to delete it!"
                                    >
                                        🗑️
                                    </div>
                                    <button 
                                        type="button"
                                        onClick={() => {
                                            setSetupBoard({});
                                            setItemForm(prev => ({...prev, fen: `8/8/8/8/8/8/8/8 ${setupTurn} - - 0 1`, answer: ''}));
                                        }}
                                        style={{
                                            padding: '4px 8px',
                                            height: '28px',
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                            background: '#ef4444',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            boxShadow: 'var(--neu-shadow)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginLeft: '5px'
                                        }}
                                    >
                                        Clear 🧹
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => {
                                            const stdFen = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
                                            parseFenToBoard(stdFen);
                                            setItemForm(prev => ({...prev, fen: stdFen, answer: ''}));
                                            toast.success("Board reset to starting position!");
                                        }}
                                        style={{
                                            padding: '4px 8px',
                                            height: '28px',
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                            background: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            boxShadow: 'var(--neu-shadow)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginLeft: '4px'
                                        }}
                                    >
                                        Reset Position 🔄
                                    </button>
                                </div>
                                <div style={{
                                    width: '100%',
                                    aspectRatio: '1/1', 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(8, 1fr)', 
                                    gridTemplateRows: 'repeat(8, 1fr)', 
                                    border: setupMode === 'play' ? '4px solid #10b981' : '4px solid rgba(163,177,198,0.4)', 
                                    borderRadius: '12px', 
                                    boxShadow: 'var(--neu-shadow-inset), 0 4px 10px rgba(0,0,0,0.06)',
                                    overflow: 'hidden' 
                                }}>
                                    {baseRows.map((row, rIdx) => baseCols.map((col, cIdx) => { 
                                        const sq = `${col}${row}`; const isDark = (rIdx + cIdx) % 2 === 1;
                                        const isSelected = setupSelectedSq === sq;
                                        const isMovable = setupMode === 'play' && setupBoard[sq] && setupBoard[sq][0] === (itemForm.fen.split(' ')[1] || setupTurn);
                                        return ( 
                                            <div 
                                                key={sq} 
                                                onClick={() => handleBoardClick(sq)} 
                                                data-square={sq}
                                                style={{
                                                    background: isSelected ? 'rgba(59, 130, 246, 0.85)' : isDark ? '#769656' : '#eeeed2', 
                                                    display: 'flex', 
                                                    justifyContent: 'center', 
                                                    alignItems: 'center', 
                                                    cursor: setupMode==='play'? (isMovable || isSelected ? 'pointer' : 'default') : 'grab',
                                                    position: 'relative',
                                                    outline: setupMode === 'play' && isMovable && !isSelected ? '2px solid rgba(16,185,129,0.5)' : 'none'
                                                }}
                                            >
                                                {setupBoard[sq] && (
                                                    <img 
                                                        src={PIECE_IMAGES[setupBoard[sq]]} 
                                                        alt="p" 
                                                        draggable={false}
                                                        onPointerDown={(e) => setupMode === 'place' ? handlePieceDragStart(e, setupBoard[sq], sq) : undefined}
                                                        style={{
                                                            width: '90%', 
                                                            height: '90%', 
                                                            cursor: setupMode === 'play' ? (isMovable ? 'pointer' : 'default') : 'grab',
                                                            touchAction: 'none'
                                                        }} 
                                                    />
                                                )}
                                            </div> 
                                        ) 
                                    }))}
                                </div>
                                <div style={{display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                    <button 
                                        onClick={() => { setSetupTurn('w'); updateFenFromBoard(setupBoard, 'w', setupCastling); }} 
                                        style={{
                                            padding: '8px 16px', 
                                            background: setupTurn === 'w' ? 'var(--btn-hover)' : 'var(--btn-bg)', 
                                            color: 'var(--text-primary)', 
                                            border: '1px solid rgba(255, 255, 255, 0.55)', 
                                            borderRadius: '8px', 
                                            cursor: 'pointer', 
                                            fontWeight: 'bold',
                                            boxShadow: setupTurn === 'w' ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                                            transition: 'all 0.15s'
                                        }}
                                    >
                                        ⚪ White
                                    </button>
                                    <button 
                                        onClick={() => { setSetupTurn('b'); updateFenFromBoard(setupBoard, 'b', setupCastling); }} 
                                        style={{
                                            padding: '8px 16px', 
                                            background: setupTurn === 'b' ? 'var(--btn-hover)' : 'var(--btn-bg)', 
                                            color: 'var(--text-primary)', 
                                            border: '1px solid rgba(255, 255, 255, 0.55)', 
                                            borderRadius: '8px', 
                                            cursor: 'pointer', 
                                            fontWeight: 'bold',
                                            boxShadow: setupTurn === 'b' ? 'var(--neu-shadow-inset)' : 'var(--neu-shadow)',
                                            transition: 'all 0.15s'
                                        }}
                                    >
                                        ⚫ Black
                                    </button>
                                </div>

                                {/* 🏰 CASTLING RIGHTS CONTROLS */}
                                <div style={{ 
                                    background: '#e0e5ec', 
                                    padding: '12px 16px', 
                                    borderRadius: '12px', 
                                    boxShadow: 'var(--neu-shadow-inset)', 
                                    border: '1px solid rgba(255, 255, 255, 0.55)', 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: '8px',
                                    marginTop: '5px'
                                }}>
                                    <span style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)' }}>🏰 Castling Rights Allowed:</span>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px', fontWeight: '600' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-primary)' }}>
                                            <input type="checkbox" checked={setupCastling.K} onChange={() => toggleCastling('K')} style={{ accentColor: '#3b82f6', width: '14px', height: '14px', cursor: 'pointer' }} /> White O-O
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-primary)' }}>
                                            <input type="checkbox" checked={setupCastling.Q} onChange={() => toggleCastling('Q')} style={{ accentColor: '#3b82f6', width: '14px', height: '14px', cursor: 'pointer' }} /> White O-O-O
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-primary)' }}>
                                            <input type="checkbox" checked={setupCastling.k} onChange={() => toggleCastling('k')} style={{ accentColor: '#3b82f6', width: '14px', height: '14px', cursor: 'pointer' }} /> Black O-O
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-primary)' }}>
                                            <input type="checkbox" checked={setupCastling.q} onChange={() => toggleCastling('q')} style={{ accentColor: '#3b82f6', width: '14px', height: '14px', cursor: 'pointer' }} /> Black O-O-O
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <form onSubmit={handleCreateItem} style={{flex: 1, minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <div><label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', display: 'block' }}>Save Location</label><select value={itemForm.targetFolderId} onChange={(e) => setItemForm({...itemForm, targetFolderId: e.target.value})} style={inputStyle}>{getWritableFolders().map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
                                <div><label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', display: 'block' }}>Type</label><div style={{display: 'flex', gap: '10px' }}><label style={{color: 'var(--text-primary)', cursor: 'pointer' }}><input type="radio" checked={itemForm.type === 'puzzle'} onChange={() => setItemForm({...itemForm, type: 'puzzle'})} /> 🎯 Puzzle</label><label style={{color: 'var(--text-primary)', cursor: 'pointer' }}><input type="radio" checked={itemForm.type === 'position'} onChange={() => setItemForm({...itemForm, type: 'position'})} /> ♟️ Position</label></div></div>
                                <div><label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', display: 'block' }}>Title</label><input type="text" value={itemForm.title} onChange={e=>setItemForm({...itemForm, title: e.target.value})} required placeholder="e.g. Find the fork!" style={inputStyle} /></div>
                                <div><label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', display: 'block' }}>FEN String</label><input type="text" value={itemForm.fen} onChange={e => { setItemForm({...itemForm, fen: e.target.value}); parseFenToBoard(e.target.value); }} required style={{...inputStyle, color: '#10b981', fontFamily: 'monospace'}} /></div>
                                {itemForm.type === 'puzzle' && ( 
                                    <><div style={{flex: 1}}><label style={{color: '#10b981', fontSize: '13px', fontWeight: 'bold', display: 'block' }}>Answer</label><textarea value={itemForm.answer} onChange={e=>setItemForm({...itemForm, answer: e.target.value})} required placeholder="Play moves on board to auto-fill..." style={{...inputStyle, height: '80px', resize: 'none', border: '1px solid #10b981'}} /></div>
                                    <div><label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', display: 'block' }}>XP Points</label><input type="number" value={itemForm.points} onChange={e=>setItemForm({...itemForm, points: e.target.value})} min="1" style={inputStyle} /></div></> 
                                )}
                                <button type="submit" style={{padding: '15px', background: '#f59e0b', color: 'black', fontSize: '16px', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer', marginTop: 'auto' }}>Save to Selected Folder</button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {showUploadModal && (
                <div style={{position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.85)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', boxSizing: 'border-box' }}>
                    <form onSubmit={handleBulkUpload} style={{background: 'var(--bg-primary)', width: '100%', maxWidth: '600px', padding: '25px', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '15px', border: '1px solid rgba(163, 177, 198, 0.4)', maxHeight: '90vh', overflowY: 'auto' }}>
                        <h2 style={{margin: 0, color: '#10b981', display: 'flex', justifyContent: 'space-between' }}>📥 Bulk PGN Upload<span onClick={() => setShowUploadModal(false)} style={{cursor: 'pointer', color: '#ef4444' }}>✖</span></h2>
                        <div><label style={{color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', display: 'block' }}>Save Location</label><select value={uploadTargetFolder} onChange={(e) => setUploadTargetFolder(e.target.value)} style={inputStyle}>{getWritableFolders().map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
                        <input type="file" accept=".pgn" onChange={handlePgnFileChange} style={{padding: '10px', background: 'var(--bg-primary)', color: 'var(--text-primary)', borderRadius: '6px', border: '1px solid #3b82f6', cursor: 'pointer' }} />
                        <div style={{textAlign: 'center', color: '#64748b', fontWeight: 'bold' }}>OR</div>
                        <textarea value={uploadPgnData} onChange={e=>setUploadPgnData(e.target.value)} required placeholder='[Event "Tactics"]&#10;1. e4 e5 2. Nf3...' style={{height: '180px', padding: '15px', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid rgba(163, 177, 198, 0.4)', resize: 'none', fontFamily: 'monospace' }} />
                        <button type="submit" style={{padding: '15px', background: '#10b981', color: 'black', fontSize: '16px', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Extract & Save</button>
                    </form>
                </div>
            )}
        </div>
    );
}
